import { CountryCode } from "Infrastructure/Api/Api";
import type { CodeListItem } from "Models/ICodeListDto";
import type { RootStateType } from "State/Store";
import { useCountryNameResolver } from "Translations/countryNames";
import { useUiLanguage } from "Hooks/useUiLanguage";
import { useMemo } from "react";
import { useSelector } from "react-redux";

/**
 * Countries pinned to the top of every country picker, in this order: the home markets and the
 * neighbours most of our users come from (ZS-670). Everything else follows alphabetically in the
 * UI language.
 */
const PINNED_COUNTRIES: ReadonlyArray<CountryCode> = [
  CountryCode.CZ,
  CountryCode.SK,
  CountryCode.PL,
  CountryCode.AT,
  CountryCode.DE,
];

/**
 * Pinned countries first (in PINNED_COUNTRIES order), the rest sorted by their localized name.
 * Sorting by name has to happen here, not on the API — the API sends codes only. Each name is
 * resolved once; the sort compares the resolved names.
 */
const toSortedCountryItems = (
  codes: ReadonlyArray<CountryCode>,
  getName: (code: CountryCode) => string,
  language: string,
): Array<CodeListItem<CountryCode>> => {
  const items = codes.map((code) => ({ code, name: getName(code) }));
  const pinned = PINNED_COUNTRIES.flatMap((pinnedCode) =>
    items.filter(({ code }) => code === pinnedCode),
  );
  const rest = items
    .filter(({ code }) => !PINNED_COUNTRIES.includes(code))
    .toSorted((a, b) => a.name.localeCompare(b.name, language));

  return [...pinned, ...rest];
};

export const useCountryList = () => {
  const { getCountryName } = useCountryNameResolver();
  const language = useUiLanguage();

  const countries = useSelector(
    (s: RootStateType) => s.codeList.codeLists?.countries,
  );

  const codeListCountries = useMemo(
    () => toSortedCountryItems(countries ?? [], getCountryName, language),
    [countries, getCountryName, language],
  );

  return { codeListCountries };
};
